/**
 * Minimal collection-flow client for the applicant SPA. Deliberately tiny — no
 * generated client, no query cache — because the applicant surface is exactly
 * two endpoints and everything derives from the case-bound bearer token.
 *
 * `FRONTEND_API_BASE` lets the SPA point at the backend when served from a
 * different origin (dev: http://localhost:8080); empty = same origin (the
 * embed/edge proxy forwards `/api/*`).
 */
import type { InteractionBatch } from './interactions';
import { type BeaconReason, collectTelemetry, type Telemetry } from './telemetry';
import type {
  ChallengeReportFrame,
  CollectionFileRef,
  CollectionFileUpload,
  CollectionFlowSnapshot,
  CollectionStartResponse,
  LivenessSession,
  LivenessSubmission,
  PhoneOtpSent,
  PhoneOtpVerdict,
  RealtimeDescriptor,
  SignatureDescriptor,
} from './types';

const API_BASE = import.meta.env.FRONTEND_API_BASE?.replace(/\/$/, '') ?? '';

/**
 * Absolutise a server-supplied API path for use somewhere `fetch` is not doing
 * the work — an `<img src>`, say.
 *
 * The backend returns paths, not absolute URLs, so it stays correct behind any
 * proxy. Anything rendered directly by the browser has to be joined to
 * `API_BASE` here, or it silently resolves against the SPA's own origin and
 * 404s wherever the two differ.
 */
export function apiUrl(path: string): string {
  return `${API_BASE}${path}`;
}

export function authHeaders(token: string): Record<string, string> {
  return { authorization: `Bearer ${token}`, 'content-type': 'application/json' };
}

export class CollectionFlowError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    /**
     * The backend's own `{code}`, when the caller bothered to read it.
     *
     * A status alone cannot separate "that number is not a phone number" from
     * "we cannot reach the SMS service" — both are a 4xx/5xx to `fetch`, and
     * only one of them is something the applicant can fix. Optional because
     * every pre-existing call site raises without it.
     */
    public readonly code?: string,
    /** The backend's `details`, when it sent any (e.g. the applicant's place on
     *  a `location_not_allowed`). */
    public readonly details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'CollectionFlowError';
  }
}

async function parse(response: Response): Promise<CollectionFlowSnapshot> {
  if (!response.ok) {
    // Read the body's `{code, details}`: a 403 can be "this token is not yours"
    // or "not from this country / through a VPN", and only the latter has a
    // screen of its own. An unreadable body keeps the bare status.
    const body = (await response.json().catch(() => null)) as {
      code?: unknown;
      details?: unknown;
    } | null;
    throw new CollectionFlowError(
      response.status,
      `collection-flow ${response.status}`,
      typeof body?.code === 'string' ? body.code : undefined,
      body?.details && typeof body.details === 'object'
        ? (body.details as Record<string, unknown>)
        : undefined,
    );
  }
  return (await response.json()) as CollectionFlowSnapshot;
}

/** Fetch the current step for the token's case (read-only). */
export async function getCollectionFlow(token: string): Promise<CollectionFlowSnapshot> {
  const response = await fetch(`${API_BASE}/api/v1/collection-flow`, {
    headers: { authorization: `Bearer ${token}` },
  });
  return parse(response);
}

/**
 * Fire an event to advance the flow; returns the next step's snapshot.
 *
 * Every event carries `telemetry` (./telemetry) — beside `payload`, never inside
 * it: the server validates two payloads strictly and forwards payloads to the
 * statechart. Collection is best-effort; a throw there sends the event without it.
 */
export async function postCollectionFlowEvent(
  token: string,
  type: string,
  payload?: unknown,
): Promise<CollectionFlowSnapshot> {
  let telemetry: Telemetry | undefined;
  try {
    telemetry = collectTelemetry(type);
  } catch {
    telemetry = undefined;
  }
  const response = await fetch(`${API_BASE}/api/v1/collection-flow/events`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ type, payload, telemetry }),
  });
  return parse(response);
}

/**
 * Collection tracing: a batch of the applicant's clicks and screen changes
 * (./interactions). `keepalive` so the last batch survives the page closing.
 * Fire-and-forget: tracing never interrupts the applicant.
 */
export function sendInteractions(token: string, batch: InteractionBatch): void {
  try {
    void fetch(`${API_BASE}/api/v1/collection-flow/interactions`, {
      method: 'POST',
      keepalive: true,
      headers: authHeaders(token),
      body: JSON.stringify(batch),
    }).catch(() => undefined);
  } catch {
    /* never throws into the page */
  }
}

/**
 * Real-user-monitoring beacon: the applicant is leaving the step (./telemetry
 * `armExitBeacon`). `keepalive` so it survives the page closing, and NOT
 * `sendBeacon`, which cannot carry the bearer header (see ./reportAttempt).
 * Fire-and-forget: every failure is swallowed.
 */
export function sendRumBeacon(token: string, reason: BeaconReason, telemetry: Telemetry): void {
  try {
    void fetch(`${API_BASE}/api/v1/collection-flow/rum`, {
      method: 'POST',
      keepalive: true,
      headers: authHeaders(token),
      body: JSON.stringify({ reason, telemetry }),
    }).catch(() => undefined);
  } catch {
    /* never throws into the page */
  }
}

/**
 * Open a collection session: stamps started_at, returns the current snapshot +
 * the realtime channel descriptor + a short-lived desktop→mobile handoff URL.
 * `flowId` is the case id (from the snapshot); the backend 403s if it isn't the
 * token's own runtime.
 */
export async function startCollection(
  token: string,
  flowId: string,
): Promise<CollectionStartResponse> {
  const response = await fetch(`${API_BASE}/api/v1/flow/${flowId}/collection/start`, {
    method: 'POST',
    headers: authHeaders(token),
  });
  if (!response.ok) {
    throw new CollectionFlowError(response.status, `collection/start ${response.status}`);
  }
  return (await response.json()) as CollectionStartResponse;
}

/**
 * Submit captured liveness evidence REFS (never a score — the score is computed
 * server-side). Merges them into the flow context and advances past
 * awaiting_liveness. Returns the next snapshot.
 */
export async function submitLiveness(
  token: string,
  flowId: string,
  body: LivenessSubmission,
): Promise<CollectionFlowSnapshot> {
  // Same best-effort rule as `postCollectionFlowEvent`: a throw while collecting
  // sends the capture without telemetry, never blocks it.
  let telemetry: Telemetry | undefined;
  try {
    telemetry = collectTelemetry('LIVENESS_SUBMITTED');
  } catch {
    telemetry = undefined;
  }
  const response = await fetch(`${API_BASE}/api/v1/flow/${flowId}/collection/liveness`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ ...body, telemetry }),
  });
  return parse(response);
}

/**
 * Upload captured media (selfie / PAD video) into private storage and get back an
 * opaque file id. Token-bound — the case derives from the token, so there is no
 * path param and no IDOR surface. The bytes never re-enter the browser; the id is
 * resolved server-side (only for the trusted face-match adapter).
 */
export async function uploadCollectionFile(
  token: string,
  body: CollectionFileUpload,
): Promise<CollectionFileRef> {
  const response = await fetch(`${API_BASE}/api/v1/collection-flow/files`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    throw new CollectionFlowError(response.status, `collection-flow/files ${response.status}`);
  }
  return (await response.json()) as CollectionFileRef;
}

/**
 * OAuth-style return leg: once the flow is finished (terminal or manual
 * review), ask the backend for the partner callback URL. `null` means no
 * return leg is registered on this session (404) — keep the normal terminal
 * screen. 409 (flow still collecting) also resolves to `null` so a premature
 * call is harmless. The redirect target is assembled SERVER-side from the
 * mint-time-validated return_url — the SPA never builds it from context.
 */
export async function returnToPartner(token: string): Promise<{ redirect_to: string } | null> {
  const response = await fetch(`${API_BASE}/api/v1/collection-flow/return`, {
    method: 'POST',
    headers: authHeaders(token),
  });
  if (response.status === 404 || response.status === 409) return null;
  if (!response.ok) {
    throw new CollectionFlowError(response.status, `collection-flow/return ${response.status}`);
  }
  return (await response.json()) as { redirect_to: string };
}

/**
 * What the applicant is about to sign. Token-bound with no path params — the
 * case derives from the token and the file id derives from the case's own
 * pending request, so there is no id for a caller to tamper with.
 *
 * `null` on 404, which the backend uses as its "nothing to do" convention (the
 * same shape `returnToPartner` uses): the case has no pending signature
 * request. That is NOT the same as an error, and the ceremony must not invent a
 * document to sign in its place.
 */
export async function getSignatureDescriptor(token: string): Promise<SignatureDescriptor | null> {
  const response = await fetch(`${API_BASE}/api/v1/collection-flow/signature`, {
    headers: { authorization: `Bearer ${token}` },
  });
  if (response.status === 404) return null;
  if (!response.ok) {
    throw new CollectionFlowError(response.status, `collection-flow/signature ${response.status}`);
  }
  return (await response.json()) as SignatureDescriptor;
}

/**
 * The decrypted bytes of the document being signed, as a Blob for
 * `URL.createObjectURL`.
 *
 * Fetched rather than pointed at: the route requires the bearer token, and a
 * bare `<iframe src>` / `<img src>` sends no Authorization header. The bytes are
 * also stored encrypted, so there is no storage URL to link to — the API
 * decrypts and streams, and audits every read.
 */
export async function getSignatureDocument(token: string): Promise<Blob> {
  const response = await fetch(`${API_BASE}/api/v1/collection-flow/signature/document`, {
    headers: { authorization: `Bearer ${token}` },
  });
  if (!response.ok) {
    throw new CollectionFlowError(
      response.status,
      `collection-flow/signature/document ${response.status}`,
    );
  }
  return await response.blob();
}

/**
 * Realtime connection descriptor for this applicant token's case (channel +
 * Soketi host/key). Desktop and phone both call this after load so a QR scan
 * can open a private channel without re-minting a handoff token.
 */
export async function getRealtime(token: string): Promise<RealtimeDescriptor> {
  const response = await fetch(`${API_BASE}/api/v1/collection-flow/realtime`, {
    headers: { authorization: `Bearer ${token}` },
  });
  if (!response.ok) {
    throw new CollectionFlowError(response.status, `collection-flow/realtime ${response.status}`);
  }
  return (await response.json()) as RealtimeDescriptor;
}

/**
 * Pusher/Soketi private-channel authorizer. `pusher-js` calls this with the
 * socket id + channel name; the backend signs only the caller's own flow channel.
 * Returns the Pusher `{ auth }` envelope.
 */
export async function pusherAuth(
  token: string,
  socketId: string,
  channelName: string,
): Promise<{ auth: string }> {
  const response = await fetch(`${API_BASE}/api/v1/collection-flow/pusher/auth`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ socket_id: socketId, channel_name: channelName }),
  });
  if (!response.ok) {
    throw new CollectionFlowError(response.status, `pusher/auth ${response.status}`);
  }
  return (await response.json()) as { auth: string };
}

/** One message of the session chat, as the applicant sees it — never an operator's name. */
export interface ChatMessage {
  id: string;
  sender: 'operator' | 'applicant';
  body: string;
  created_at: string;
  /** When the other side opened the thread after it (null = not yet). */
  read_at: string | null;
}

export interface ChatThread {
  messages: ChatMessage[];
  /** True once the reviewing team has written: the applicant can reply. */
  can_reply: boolean;
}

/**
 * The session chat with the reviewing team (case-bound by the token). `markRead`
 * only while the chat is open — a closed launcher checks for news without
 * telling the team its messages were seen.
 */
export async function getChatThread(token: string, markRead = false): Promise<ChatThread> {
  const suffix = markRead ? '?mark_read=true' : '';
  const response = await fetch(`${API_BASE}/api/v1/collection-flow/messages${suffix}`, {
    headers: authHeaders(token),
  });
  if (!response.ok) {
    throw new CollectionFlowError(response.status, `messages ${response.status}`);
  }
  return (await response.json()) as ChatThread;
}

export async function postChatMessage(token: string, body: string): Promise<ChatThread> {
  const response = await fetch(`${API_BASE}/api/v1/collection-flow/messages`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ body }),
  });
  if (!response.ok) {
    let code: string | undefined;
    try {
      code = ((await response.json()) as { code?: string }).code;
    } catch {
      code = undefined;
    }
    throw new CollectionFlowError(response.status, `messages ${response.status}`, code);
  }
  return (await response.json()) as ChatThread;
}

/**
 * Phone verification — three calls, all case-bound by the token like the rest
 * of this surface. No case id is sent, and none is accepted.
 *
 * They read the backend's `{code}` because the applicant-facing difference
 * between these failures is real: a malformed number is theirs to fix, an
 * unreachable SMS service is not, and a rate limit is neither — it is a wait.
 */
async function phoneCall<T>(path: string, token: string, body?: unknown): Promise<T> {
  const response = await fetch(`${API_BASE}/api/v1/collection-flow/phone/${path}`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(body ?? {}),
  });
  if (!response.ok) {
    let code: string | undefined;
    try {
      code = ((await response.json()) as { code?: string }).code;
    } catch {
      // A body that is not JSON tells us nothing extra; the status still does.
    }
    throw new CollectionFlowError(response.status, `collection-flow/phone/${path}`, code);
  }
  return (await response.json()) as T;
}

/** Ask for a code. Spends an SMS, so the UI must not call it speculatively. */
export async function sendPhoneOtp(token: string, phone: string): Promise<PhoneOtpSent> {
  return phoneCall<PhoneOtpSent>('send', token, { phone });
}

/**
 * Check a code. A WRONG code is a 200 with `verified: false` — only a request
 * that could not be answered at all throws, which is why the caller must read
 * the verdict rather than treat "no exception" as success.
 */
export async function verifyPhoneOtp(token: string, code: string): Promise<PhoneOtpVerdict> {
  return phoneCall<PhoneOtpVerdict>('verify', token, { code });
}

/** Invalidate the live code and send a new one to the same number. */
export async function resendPhoneOtp(token: string): Promise<PhoneOtpSent> {
  return phoneCall<PhoneOtpSent>('resend', token);
}

/**
 * Start (or resume) the server-owned active-liveness challenge session.
 *
 * Returns ONE challenge. The rest of the sequence is not revealed and is not
 * knowable client-side — that unpredictability is the whole anti-replay
 * property, so a helper that fetched "the remaining challenges" would undo it.
 *
 * Calling this twice RESUMES: the server returns the outstanding challenge
 * rather than drawing a new one, so a page reload does not become a re-roll.
 */
export async function startLivenessSession(
  token: string,
  options: { flash?: boolean } = {},
): Promise<LivenessSession> {
  // `flash: false` = the applicant is sensitive to flashing light: the server
  // then plans no flash step, and records that it was declined.
  // ALWAYS a body: `authHeaders` declares JSON, and the server parses a declared
  // JSON body — an empty one is a 400, which stopped every session start that
  // did not decline the flash. `{}` means "no options".
  const declined = options.flash === false;
  const response = await fetch(`${API_BASE}/api/v1/collection-flow/liveness-session`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(declined ? { flash: false } : {}),
  });

  if (!response.ok) {
    throw new CollectionFlowError(response.status, `liveness-session ${response.status}`);
  }

  return (await response.json()) as LivenessSession;
}

/**
 * Answer the outstanding challenge; receive the next one, or the verdict.
 *
 * The body carries NO action field. The server knows which challenge it issued
 * and re-derives satisfaction from the reported numbers — telling it what we
 * think we performed would hand the client back the authority this whole
 * mechanism exists to take away.
 */
export async function submitLivenessStep(
  token: string,
  sessionId: string,
  challengeId: string,
  frames: ChallengeReportFrame[],
): Promise<LivenessSession> {
  const response = await fetch(`${API_BASE}/api/v1/collection-flow/liveness-session/step`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ sessionId, challengeId, frames }),
  });

  if (!response.ok) {
    throw new CollectionFlowError(response.status, `liveness-step ${response.status}`);
  }

  return (await response.json()) as LivenessSession;
}

/** Why a reusable link did not open a session. */
export type LinkSessionError = 'not_found' | 'retry_limit' | 'unavailable';

/**
 * Start (or resume) a session through a reusable workflow link. No credential:
 * the link id is the bearer. Returns the applicant flow URL.
 */
export async function startLinkSession(
  linkId: string,
  deviceId: string,
  language?: string,
): Promise<{ url: string } | { error: LinkSessionError }> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE}/api/v1/links/${encodeURIComponent(linkId)}/sessions`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ device_id: deviceId, language }),
    });
  } catch {
    return { error: 'unavailable' };
  }
  if (response.ok) {
    const body = (await response.json()) as { url: string };
    return { url: body.url };
  }
  if (response.status === 404) return { error: 'not_found' };
  if (response.status === 403) return { error: 'retry_limit' };
  return { error: 'unavailable' };
}
